export enum ESalesAssistClientStatusFilter {
    All = 'all',
    EiRecord = 'ei_record',
    New = 'new',
    Existing = 'existing',
    Past = 'past',
}

export enum ESalesAssistSearchMode {
    City = 'city',
    NearMe = 'nearMe',
    ChooseLocation = 'chooseLocation',
}

export interface ISalesAssistMapPoint {
    latitude: number
    longitude: number
}

export interface ISalesAssistPlaceHit {
    label: string
    shortLabel: string
    latitude: number
    longitude: number
    south: number
    north: number
    west: number
    east: number
    addressType: string
}

export interface ISalesAssistMapSchoolMarker {
    id: string
    name: string
    latitude: number
    longitude: number
}

export enum ESalesAssistQualification {
    All = 'all',
    Target = 'target',
    Qualified = 'qualified',
    Watching = 'watching',
}

export enum ESalesAssistStatusBadge {
    EiClient = 'eiClient',
    Target = 'target',
    New = 'new',
    Existing = 'existing',
    PastClient = 'pastClient',
    EiRecord = 'eiRecord',
    PossibleMatch = 'possibleMatch',
    Qualified = 'qualified',
    Watching = 'watching',
    AiEnriched = 'aiEnriched',
}

export enum ESalesAssistMatchStatus {
    Pending = 'pending',
    Approve = 'approve',
    Reject = 'reject',
}

export enum ESalesAssistMatchDisplayStatus {
    New = 'new',
    Existing = 'existing',
    PossibleMatch = 'possible_match',
}

export enum ESchoolDiscoverySource {
    LocalStore = 'local_store',
    GoogleMaps = 'google_maps',
    Gemini = 'gemini',
}

export enum ESalesAssistEnrichmentConfidence {
    High = 'high',
    Medium = 'medium',
    Low = 'low',
}

export enum ESalesAssistCrmClientStatus {
    Existing = 'existing',
    Past = 'past',
    EiRecord = 'ei_record',
    New = 'new',
}

export enum ESalesAssistNearbyDiscoveryAction {
    Idle = 'idle',
    Pull = 'pull',
    Refresh = 'refresh',
    Background = 'background',
}

export type TNearbyDiscoveryState =
    | { status: 'idle' }
    | { status: 'locating' }
    | { status: 'loading'; message: string }
    | { status: 'success' }
    | { status: 'error'; message: string }

export interface ISchoolDiscoveryCrmProfile {
    clientStatus: ESalesAssistCrmClientStatus
    lastPurchaseDate: string | null
    productsBought: string[]
    totalPayableAmount: number | null
    totalExclusiveGstAmount: number | null
    totalStudents: number | null
    orderStatus: string | null
}

export interface ISalesAssistCrmProfileSummary {
    clientStatus: ESalesAssistCrmClientStatus
    lastPurchaseLabel: string | null
    products: string[]
    totalPayableAmountLabel: string | null
    totalStudentsLabel: string | null
}

export interface ISalesAssistOrderHistoryYear {
    financialYear: string
    lastPurchaseLabel: string | null
    totalValueLabel: string | null
    totalStudentsLabel: string | null
    products: string[]
    orderStatus: string | null
}

export interface ISalesAssistSchoolCard {
    id: string
    placeId?: string
    organisationCode?: number
    name: string
    badges: ESalesAssistStatusBadge[]
    board: string
    location: string
    address?: string
    city?: string
    latitude?: number
    longitude?: number
    rating?: number
    userRatingsTotal?: number
    feeRange: string
    score: number
    isRecommended: boolean
    engagementText: string
    engagementIcon: 'visit' | 'none' | 'email'
    qualifications: ESalesAssistQualification[]
    enrichment?: ISalesAssistEnrichment
    discoveredAt?: string
    matchScore?: number
    matchStatus?: ESalesAssistMatchStatus
    matchDisplayStatus?: ESalesAssistMatchDisplayStatus
    crmProfile?: ISchoolDiscoveryCrmProfile
}

export interface ISchoolDiscoveryRecord {
    id: string
    placeId?: string
    generatedId: boolean
    name: string
    city: string
    address?: string
    latitude?: number
    longitude?: number
    rating?: number
    userRatingsTotal?: number
    businessStatus?: string
    types?: string[]
    source: ESchoolDiscoverySource.GoogleMaps
    discoveredAt: string
    updatedAt: string
    searchKeys: string[]
    orgCode?: number
    matchScore?: number
    matchStatus?: ESalesAssistMatchStatus
    matchDisplayStatus?: ESalesAssistMatchDisplayStatus
    crmProfile?: ISchoolDiscoveryCrmProfile
}

export interface ISchoolDiscoveryMatchResponse {
    success: true
    school: ISchoolDiscoveryRecord
}

export interface ISchoolDiscoveryBilling {
    month: string
    mapsCalls: number
    geminiCalls: number
    mapsRemainingBeforeHardStop: number
    geminiRemainingBeforeHardStop: number
}

export interface ISchoolDiscoverySearchResponse {
    success: true
    source: ESchoolDiscoverySource.LocalStore | ESchoolDiscoverySource.GoogleMaps
    searchKey: string
    city: string
    query: string
    count: number
    mapsCallsUsed: number
    stoppedBecauseBatchLimit: boolean
    hasMoreResults: boolean
    deepSearch?: boolean
    queryVariantsUsed?: number
    schools: ISchoolDiscoveryRecord[]
    enrichments?: Record<string, ISalesAssistEnrichment>
    billing: ISchoolDiscoveryBilling
}

export interface ISalesAssistEnrichmentData {
    principal_name?: string | null
    principal_email?: string | null
    principal_phone?: string | null
    school_email?: string | null
    school_phone?: string | null
    website?: string | null
    board?: string | null
    state?: string | null
    pincode?: string | null
    catering_classes?: string | null
    student_count?: number | null
    teacher_count?: number | null
    school_age?: string | null
    fee_range?: string | null
    competitors?: string | null
    mandatory_disclosures?: string | null
    latitude?: number | null
    longitude?: number | null
    confidence?: ESalesAssistEnrichmentConfidence | string | null
    sources?: string[]
}

export interface ISalesAssistEnrichmentGroundingMetadata {
    sources?: string[]
}

export interface ISalesAssistEnrichment {
    schoolId: string
    enrichedAt: string
    model: string
    promptVersion: string
    inputSnapshot: {
        name: string
        city: string
        address?: string
        latitude?: number
        longitude?: number
        rating?: number
    }
    scrapedContextChars: number
    data: ISalesAssistEnrichmentData
    groundingMetadata?: ISalesAssistEnrichmentGroundingMetadata
}

export interface ISalesAssistEnrichmentPresentationContext {
    page: number
    pageSize: number
}

export interface ISchoolDiscoveryEnrichResponse {
    success: true
    source: ESchoolDiscoverySource.LocalStore | ESchoolDiscoverySource.Gemini
    school: ISchoolDiscoveryRecord
    enrichment: ISalesAssistEnrichment
    billing: ISchoolDiscoveryBilling
}

export interface ISchoolDiscoveryStoredResponse {
    success: true
    source: ESchoolDiscoverySource.LocalStore
    count: number
    schools: ISchoolDiscoveryRecord[]
    enrichments: Record<string, ISalesAssistEnrichment>
    billing: ISchoolDiscoveryBilling
}
